import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { getDraftOrders, type DraftSummary } from '@/api/orderPrep';
import { useAuth } from '@/auth/useAuth';
import { useOrdersDataStore } from '@/stores/ordersDataStore';
import { PageHeader } from '@/components/PageHeader';
import { buildCalendarItems, todayDay } from '@/features/overview/calendarItems';
import { OverviewCalendar } from '@/features/overview/OverviewCalendar';
import { PastDue } from '@/features/overview/PastDue';
import { WINDOW_DAYS, moneyCompact, stageRows, type StageRow, type StageValue } from '@/features/overview/overviewStats';
import { PageState } from './PageState';
import { StatusPill } from '@/components/Status';
import { WorkNotice } from '@/features/orders/workLib';
import { cn } from '@/lib/utils';

const SUB = 'mt-1 inline-block leading-[1.5] text-muted-foreground';
const PANEL = 'overflow-hidden rounded-[10px] border border-border bg-card shadow-card';

/**
 * The one page about time. The work column already says how big every queue
 * is, so this page shows what the queues cannot: each stage's journeys and
 * money side by side; then a full-width calendar of what is dated over the
 * next weeks, and everything past its date under it. Nothing here duplicates a
 * queue's rows.
 */

/**
 * The stage's money on one line: the USD total in the display face, then every
 * other currency the stage carries beside it. Nothing is ever added across
 * currencies — the figures sit side by side — so a stage that is part EUR
 * prints both totals instead of a note about the part it left out.
 */
function MoneyLine({ value }: { value: StageValue }) {
  const figures = [
    ...(value.amount != null ? [{ currency: 'USD', amount: value.amount }] : []),
    ...value.others,
  ];
  if (figures.length === 0) return null;
  const [lead, ...rest] = figures;
  return (
    <p>
      <strong className="text-[14px] font-semibold text-foreground tabular-nums">
        {value.amount != null && value.approx ? '~' : ''}{moneyCompact(lead.amount, lead.currency)}
      </strong>{' '}
      {value.label}
      {rest.map((f) => (
        <span key={f.currency} className="ml-1.5 whitespace-nowrap tabular-nums">+{moneyCompact(f.amount, f.currency)}</span>
      ))}
    </p>
  );
}

/**
 * The card's single column is minmax(0,1fr): an implicit auto column grows to
 * its widest unbreakable child, and a long attention pill ("12 parcels
 * arriving this week") would push the header's arrow past the card's edge.
 * The pills wrap instead of truncating, so the fact stays whole.
 */
function StageCards({ rows, withMoney }: { rows: StageRow[]; withMoney: boolean }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,180px),1fr))] gap-3">
      {rows.map((row) => (
        <article className="row-span-5 grid min-w-0 grid-cols-[minmax(0,1fr)] grid-rows-subgrid items-start gap-y-0 rounded-[10px] border border-border bg-card px-4 pb-3 pt-3.5 text-[12px]" key={row.stage} aria-labelledby={`stage-${row.stage}`}>
          <Link to={row.to} className="row-start-1 block rounded-[4px] hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-[5px] focus-visible:outline-ring">
            <div className="flex items-center justify-between gap-2">
              <h3 id={`stage-${row.stage}`} className="text-[13px] font-semibold">{row.title}</h3>
              <ArrowUpRight aria-hidden="true" className="size-[15px] text-muted-foreground" />
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-display text-[30px] leading-none font-extrabold tracking-[-.03em] tabular-nums">{row.count.toLocaleString()}</span>
              <span className="text-muted-foreground">{row.count === 1 ? 'journey' : 'journeys'}</span>
            </div>
          </Link>
          {row.countSub && <p className={cn(SUB, 'row-start-2')}>{row.countSub}</p>}
          {withMoney && row.value && (row.value.amount != null || row.value.others.length > 0 || row.value.sub) && (
            <div className="row-start-3 mt-2.5 leading-[1.5] text-muted-foreground">
              <MoneyLine value={row.value} />
              {row.value.sub && <p className={SUB}>{row.value.sub}</p>}
            </div>
          )}
          {row.attention.length > 0 && (
            <ul className="row-start-4 mt-2.5 flex list-none flex-wrap gap-1.5 p-0" aria-label="Attention">
              {row.attention.map((fact) => <li key={fact.text} className="max-w-full"><StatusPill tone={fact.tone ?? 'neutral'} className="whitespace-normal [&>span]:whitespace-normal">{fact.text}</StatusPill></li>)}
            </ul>
          )}
          <div className="row-start-5 pt-3">
            <span className="mb-0.5 block border-t border-border pt-2.5 text-[10px] tracking-[.04em] uppercase text-muted-foreground">Last {WINDOW_DAYS} days</span>
            <p className="leading-[1.5]">{row.recent?.text ?? 'No recent activity'}
              {withMoney && row.recent?.money != null && <span className={cn(SUB, 'mt-0 ml-1.5')}>{moneyCompact(row.recent.money)}</span>}
            </p>
          </div>
        </article>
      ))}
    </div>
  );
}

export function WorkOverviewPage() {
  const { can } = useAuth();
  const { overview, orders, releases, loading, error, load, lastLoadedAt } = useOrdersDataStore();
  const [drafts, setDrafts] = useState<DraftSummary[] | null>(null);
  const [draftError, setDraftError] = useState(false);
  // Draft sizing and money are the two sensitive reads: the server nulls or
  // refuses them without the code, so the page asks only when it may.
  const canDrafts = can('orders:read:drafts');
  const canCosts = can('orders:read:costs');
  useEffect(() => {
    if (!canDrafts) return;
    let cancelled = false;
    getDraftOrders().then((rows) => {
      if (!cancelled) { setDrafts(rows); setDraftError(false); }
    }).catch(() => {
      if (!cancelled) { setDrafts(null); setDraftError(true); }
    });
    return () => { cancelled = true; };
  }, [canDrafts, lastLoadedAt]);

  const today = todayDay();
  const items = useMemo(
    () => buildCalendarItems({ orders, overview, releases, today }),
    [orders, overview, releases, today]
  );
  const rows = useMemo(
    () => (overview ? stageRows({ orders, overview, items, drafts, today, withMoney: canCosts }) : []),
    [orders, overview, items, drafts, today, canCosts]
  );
  const pastDue = useMemo(() => items.filter((i) => i.overdue), [items]);
  const eyebrow = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div className="relative h-full overflow-y-auto bg-background">
      <div className="mx-auto max-w-[1600px] pb-8">
        <PageHeader
          eyebrow={eyebrow}
          title="Overview"
        />
        <PageState loading={loading && !overview} error={error} onRetry={() => void load()} />
        {overview && (
          <div className="grid gap-4 px-8 max-md:px-4">
            <section aria-labelledby="stages-heading">
              <StageCards rows={rows} withMoney={canCosts} />
            </section>
            {draftError && (
              <WorkNotice tone="warning" className="my-0">
                Draft readiness could not be refreshed. Open To order to retry.
              </WorkNotice>
            )}
            <section className={PANEL} aria-label="Calendar">
              <OverviewCalendar items={items} today={today} />
            </section>
            <section className={PANEL} aria-label="Past due">
              <PastDue items={pastDue} today={today} />
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
